import React from 'react';
import { isFuture } from 'date-fns';
import {
  HRInterview,
  HRRecall,
  SerpJobApplication,
} from '../../../providers/api/dtos';
import useJobPosition from '../../../job-position/hooks/use-job-position/useJobPosition';
import {
  JobApplicationStatus,
  ReferenceType,
} from '../../../domain/types';
import {
  jobApplicationAssigneeUsers,
  jobApplicationHrUsers,
} from '../../constants';
import { goToDetailPage } from '../../../utils/navigate/utils';

function getUserDisplayName(
  email: string,
  users: { name: string; email: string }[],
): string {
  return users.find((u) => u.email === email)?.name ?? email;
}

function getUserPicture(
  email: string,
  users: { picture?: string; email: string }[],
): string | undefined {
  return users.find((u) => u.email === email)?.picture;
}

export function useJobApplicationSerpData(
  serpJobApplication: SerpJobApplication,
) {
  const {
    jobApplication, applicant, recall, interview,
  } = serpJobApplication;

  const jobPositionData = useJobPosition(jobApplication.jobPositionId);

  const calendarEvent = React.useMemo((): HRRecall | HRInterview | undefined => {
    if (!recall && !interview) return undefined;
    if (recall && !interview) return recall;
    if (!recall && interview) return interview;

    const recallDate = new Date(recall!.date);
    const interviewDate = new Date(interview!.date);

    const recallIsFuture = isFuture(recallDate);
    const interviewIsFuture = isFuture(interviewDate);

    if (recallIsFuture && interviewIsFuture) {
      return recallDate < interviewDate ? recall! : interview!;
    }

    if (recallIsFuture) return recall!;
    if (interviewIsFuture) return interview!;

    return interview!;
  }, [recall, interview]);

  const note = React.useMemo(
    () => [...jobApplication.notes].sort((a, b) => {
      const dateA = a.date ? Date.parse(a.date) : 0;
      const dateB = b.date ? Date.parse(b.date) : 0;
      return dateB - dateA;
    })[0],
    [jobApplication.notes],
  );

  const showMissedCalls = React.useMemo(
    () => [
      JobApplicationStatus.IN_PROGRESS,
      JobApplicationStatus.ASSIGNED,
    ].includes(jobApplication.status),
    [jobApplication.status],
  );

  const supervisorName = React.useMemo(() => {
    if (!jobApplication.supervisorEmail) return undefined;

    return getUserDisplayName(
      jobApplication.supervisorEmail,
      [...jobApplicationHrUsers, ...jobApplicationAssigneeUsers],
    );
  }, [jobApplication.supervisorEmail]);

  const supervisorPicture = React.useMemo(() => {
    if (!jobApplication.supervisorEmail) return undefined;

    return getUserPicture(
      jobApplication.supervisorEmail,
      jobApplicationHrUsers,
    );
  }, [jobApplication.supervisorEmail]);

  const assigneeName = React.useMemo(() => {
    if (!jobApplication.assigneeEmail) return undefined;

    return getUserDisplayName(
      jobApplication.assigneeEmail,
      [...jobApplicationAssigneeUsers, ...jobApplicationHrUsers],
    );
  }, [jobApplication.assigneeEmail]);

  const goToJobApplicationPage = React.useCallback(() => {
    goToDetailPage(jobApplication.id!, ReferenceType.JOB_APPLICATION);
  }, [jobApplication.id]);

  return {
    jobApplication,
    applicant,
    jobPositionData,
    calendarEvent,
    note,
    showMissedCalls,
    supervisorName,
    supervisorPicture,
    assigneeName,
    goToJobApplicationPage,
  };
}
